import React from "react";
import { PackageCheckIcon, SparklesIcon, MapIcon, CheckIcon, ArrowRightIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { SectionHeader } from "../shared/SectionHeader";
import { Reveal } from "../shared/Reveal";

// [Trust] [Conversion] Distinguishes standard inclusions, upgrades and site costs. No specific inclusions until an
// approved schedule exists. No "us vs big builders" table.
const costTypes = [{
  title: "Standard inclusions",
  description: "Items included in the base scope for your design, set out in your project’s inclusions schedule.",
  ask: "Ask for the current schedule for your design.",
  icon: PackageCheckIcon
}, {
  title: "Optional upgrades",
  description: "Selections you choose to add beyond the standard schedule. Items in photos aren’t automatically standard.",
  ask: "Ask how upgrades are priced and when selections close.",
  icon: SparklesIcon
}, {
  title: "Site-specific costs",
  description: "Costs that depend on your land, such as soil, slope, access, services, demolition and local requirements.",
  ask: "Ask what’s needed to assess your site.",
  icon: MapIcon
}];
const checklist = [{
  title: "Scope",
  text: "Exactly what work is included, and what isn’t."
}, {
  title: "Inclusions",
  text: "The fixtures, finishes and fittings in the base price."
}, {
  title: "Exclusions",
  text: "Items you’d need to pay for separately."
}, {
  title: "Site costs",
  text: "Whether site works are estimated, allowed for or excluded."
}, {
  title: "Allowances",
  text: "Provisional sums and how differences are charged."
}, {
  title: "Variations",
  text: "How changes are requested, priced and approved."
}];
export function InclusionsClarity() {
  return <section id="inclusions" aria-labelledby="inclusions-heading" className="bg-paper py-20 md:py-28" data-id="element-256" data-mp="3pql7dgq5so38">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-257" data-mp="1rdbfjt7e878n">
        <SectionHeader id="inclusions-heading" title="No surprises on price." intro="Your build price depends on the design, your site, your selections and the agreed scope. Here’s how it breaks down, and what to check in any quote." data-id="element-258" data-mp="1vdbtzduhsifp" />

        <div className="mt-14 grid gap-6 md:grid-cols-3" data-id="element-259" data-mp="2tritv2zg4cwd">
          {costTypes.map((type, index) => <Reveal key={type.title} delay={index * 0.05} className="h-full" data-id="element-260" data-mp="37mulv2m7vp9l" data-mpi={index}>
              <div className="flex h-full flex-col items-center rounded-[24px] border border-line bg-white p-7 text-center md:p-8" data-id="element-261" data-mp="33man5ulzwdlc">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paper text-charcoal" data-id="element-262" data-mp="3coa64sgklrqx">
                  <type.icon className="h-6 w-6" aria-hidden="true" data-id="element-263" data-mp="3rdohg63pij8y" />
                </span>
                <h3 className="mt-5 text-[22px] font-semibold tracking-tight" data-id="element-264" data-mp="3mebxpu84tsal">{type.title}</h3>
                <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-265" data-mp="4879riuwrphw">{type.description}</p>
                <p className="mt-auto w-full border-t border-line pt-4 text-[15px] font-medium text-charcoal" data-id="element-266" data-mp="3vf1o2iemiqgx">{type.ask}</p>
              </div>
            </Reveal>)}
        </div>

        <Reveal className="mt-6" data-id="element-267" data-mp="nu8trysk6xnp">
          <div className="rounded-[32px] bg-charcoal p-7 text-paper md:p-12" data-id="element-268" data-mp="nuim3t12tgwk">
            <h3 className="text-center text-[24px] font-semibold tracking-tight md:text-[30px]" data-id="element-269" data-mp="9ura1xu4eal7">Comparing quotes? Check these six things.</h3>
            <p className="mt-2 text-center text-[16px] text-paper/70" data-id="element-270" data-mp="durt5vs5z9yl">Useful whichever builder you’re talking to.</p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-271" data-mp="3d6nez1sqms">
              {checklist.map(item => <li key={item.title} className="flex items-start gap-4 rounded-2xl border border-paper/15 p-5" data-id="element-272" data-mp="4x7pkg6tx1kt">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-273" data-mp="3rappdyfkxc4t">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-274" data-mp="3day4ddqnavhb" />
                  </span>
                  <span data-id="element-275" data-mp="3iakgfjuqm5oj">
                    <span className="block font-heading text-[17px] font-semibold" data-id="element-276" data-mp="3i9rin8draqij">{item.title}</span>
                    <span className="mt-1 block text-[15px] leading-relaxed text-paper/75" data-id="element-277" data-mp="3a93eb29qveku">{item.text}</span>
                  </span>
                </li>)}
            </ul>
            <div className="mt-10 text-center" data-id="element-278" data-mp="30iqmcozloj4o">
              <ButtonLink to="/contact" data-id="element-279" data-mp="30hfjqshw5hf5">
                Discuss pricing for your project <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-280" data-mp="2whyarp39a258" />
              </ButtonLink>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}